import { useEffect, useMemo, useState } from "react";
import useAssetFilterType from "domains/assets/hooks/useAssetFilterType";
import { useSearchContext } from "domains/search/contexts/SearchProvider";
import { MeilisearchResult } from "domains/search/interfaces/Search";
import _ from "lodash";

export default function useAssetFilterModels({
  privacy,
  isEnabled = true,
  variant,
}: {
  privacy: "public" | "private";
  isEnabled?: boolean;
  variant?: "image" | "3d" | "video" | "texture";
}) {
  const { getAllIndexes, searchWithTokenRenewal } = useSearchContext();
  const [wasEnabled, setWasEnabled] = useState<boolean>(isEnabled);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modelIds, setModelIds] = useState<string[]>([]);
  const [facets, setFacets] = useState<Record<string, number>>({});

  const {
    allAssetsTypeArgs: { types: assetTypes },
  } = useAssetFilterType({
    assetType: variant ?? "all",
    avoidPersistedState: true,
  });

  const targetIndex = useMemo(
    () => getAllIndexes("asset")[privacy],
    [getAllIndexes, privacy]
  );

  useEffect(() => {
    if (isEnabled && !wasEnabled) {
      setWasEnabled(true);
    }
  }, [isEnabled, wasEnabled]);

  useEffect(() => {
    if (!targetIndex) return;
    if (!searchWithTokenRenewal) return;
    if (!wasEnabled) return;

    const search = async () => {
      try {
        setIsLoading(true);
        setWasEnabled(true);

        const query = {
          indexName: targetIndex,
          params: {
            query: "",
            filters: _.compact([
              `metadata.modelId IS NOT EMPTY`,
              `metadata.type IN [${assetTypes.join(", ")}]`,
            ]).join(" AND "),
            facets: ["metadata.modelId"],
            hitsPerPage: 0,
          },
        };

        const response = await searchWithTokenRenewal([query]);
        if (!response) return;
        const results: MeilisearchResult[] = response.results;
        setModelIds((modelIds) =>
          _.intersection(
            Object.keys(results[0]?.facets?.["metadata.modelId"] ?? {}),
            modelIds
          )
        );
        setFacets(
          _.mapValues(results[0]?.facets?.["metadata.modelId"], () => 0)
        );
      } catch (error) {
        setModelIds([]);
        setFacets({});
      } finally {
        setIsLoading(false);
      }
    };
    void search();
  }, [
    targetIndex,
    searchWithTokenRenewal,
    setModelIds,
    wasEnabled,
    assetTypes,
  ]);

  if (!isEnabled) {
    return {
      modelsQueryArgs: {},
      fmHeaderFilterModelsProps: undefined,
    };
  }

  return {
    modelsQueryArgs: {
      modelIds,
    },
    fmHeaderFilterModelsProps: {
      value: modelIds,
      setValue: setModelIds,
      facets,
      isLoading,
    },
  };
}
